import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import PulseLoader from "react-spinners/PulseLoader";
import * as moment from "moment";
import DatePicker from "react-multi-date-picker";
import { Dropdown, Image } from "hesus-ui-kit";
import axios from "axios";
import { useGetGeneralParameters } from "@hooks/useGeneralParams";
import { handleApiError } from "@utils/errorHandler";
import {
  getDatePickerFormat,
  getDateFormat,
  getCalendarLocale,
} from "@utils/datePickerUtils";
import PaginationData from "@shared/baseUI/PaginationData/PaginationData";
import SolutionView from "../../Solution/SolutionView/SolutionView";
import Header from "./components/Header/Header";
import GlobalListing from "./components/ListingData/GlobalListing";
import SiteListing from "./components/ListingData/SiteListing";
import AddEditDispute from "../../Dispute/AddEditDispute";
import AddEditOtherSummary from "../../Summary/AddEditSummary/AddEditOtherSummary";
import AddEditHaulierSummary from "../../Summary/AddEditSummary/AddEditHaulierSummary";
import AddEditCentreSummary from "../../Summary/AddEditSummary/AddEditCentreSummary";
// import AddEditSupplier from "../../AddEditSupplier/AddEditSupplier";
import AddEditPlanningNotification from "../../PlanningNotification/AddEditPlanningNotification";
import DisputeView from "../../Dispute/DisputeView";
import enableIcon from "@icons/ic-enable.svg";
import disableIcon from "@icons/ic-disable.svg";
import "./GeneralPlanningList.scss";
import { labelStyles, ColumnButton } from "@utils/extra-style";
import UserInfo from "@utils/UserInfo";
import {
  PlanningListContainer,
  Title,
  FlexRow,
  SectionContainer,
  FilterFlexRow,
  FilterOuter,
  OuterWrapper,
  GridRow,
  PlanningServiceInfo,
  HeaderWrapperTable,
  GridSubLabel,
  ButtonWrapper,
} from "./GeneralPlanningList.styled";

/**
 * global planning List,
 * Path - planning/global
 * @see See [Global Planning Requirements](https://hesusstore.atlassian.net/wiki/spaces/HS/pages/3354460164/Global+planning+page)
 * @component
 */
const GeneralPlanningList = (props) => {
  const { t, page, siteId, siteStartDate } = props;

  const [isRefresh, setIsRefresh] = useState(false);
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const [planningData, setPlanningData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageStart, setPageStart] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [pageCount, setPageCount] = useState(0);

  const generalParams = useGetGeneralParameters();
  const [currentSolution, setCurrentSolution] = useState(null);
  const [isSolutionViewPopupVisible, setSolutionViewPopupVisibility] =
    useState(false);
  const [isAddDisputePopupVisible, setAddDisputePopupVisible] = useState(false);
  const [isDisputeInPopUp, setDisputeInPopUp] = useState(false);
  const [isViewDisputePopupVisible, setViewDisputePopupVisible] =
    useState(false);
  const [addDisputeLineId, setAddDisputeLineId] = useState(false);
  const [addDisputeSiteId, setAddDisputeSiteId] = useState(false);
  const [disputeEdit, setDisputeEdit] = useState(false);
  const [disputeEditId, setDisputeEditId] = useState("");
  const [planingStatus, setPlaningStatus] = useState(0);
  const [isAddPlanningNotification, setIsAddPlanningNotification] =
    useState(false);

  const [isAddSummaryPopupVisible, setAddSummaryPopupVisible] = useState(false);
  const [clientOrderItemId, setClientOrderItemId] = useState("");
  const [summaryId, setSummaryId] = useState("");
  const [editSummary, setEditSummary] = useState(false);
  const [jobsiteId, setJobsiteId] = useState(false);

  const [isAddCentreSummaryPopupVisible, setAddCentreSummaryPopupVisible] =
    useState(false);
  const [isAddHaulierSummaryPopupVisible, setAddHaulierSummaryPopupVisible] =
    useState(false);
  const [lineGroupId, setLineGroupId] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [companyName, setCompanyName] = useState("");

  const [countryId, setCountryId] = useState(UserInfo.countryId());
  const countryList = UserInfo.countryListForFilter(t);

  const pageTranslation = {
    global: "GLOBAL_PLANNING",
    site: "SITE_PLANNING",
    daily: "DAILY_PLANNING",
  };

  const changePlanningStatus = () => {
    setPlaningStatus(!planingStatus);
  };

  const fetchGlobalPlanning = () => {
    if (startDate && endDate && countryId) {
      setIsLoading(true);
      axios
        .get(
          `/suppliersPlanningLines?countries=[${countryId}]&startDate=${moment(
            startDate).format("YYYY-MM-DD")}&endDate=${moment(endDate).format(
            "YYYY-MM-DD")}&limit=${pageSize}&page=${currentPage}`
        )
        .then((response) => {
          if (response?.data?.supplierPlanningLine?.length > 0) {
            setPlanningData(response.data.supplierPlanningLine);
            setPageCount(response.data.filteredRecords);
          } else {
            setPlanningData([]);
            setPageCount(0);
          }
          setIsLoading(false);
          setIsRefresh(false);
        })
        .catch((error) => {});
    }
  };

  const fetchSitePlanning = () => {
    if (startDate && endDate && siteId) {
      setIsLoading(true);
      axios
        .get(
          `/sites/${siteId}/plannings?startDate=${moment(
            startDate
          ).format("YYYY-MM-DD")}&endDate=${moment(
            endDate
          ).format("YYYY-MM-DD")}&limit=${pageSize}&page=${currentPage}`
        )
        .then((response) => {
          if (response?.data?.sitePlanning?.length > 0) {
            setPlanningData(response.data.sitePlanning);
            setPageCount(response.data.filteredRecords);
          } else {
            setPlanningData([]);
            setPageCount(0);
          }
          setIsLoading(false);
          setIsRefresh(false);
        })
        .catch((error) => {});
    }
  };

  const fetchDailyPlanning = () => {
    if (startDate && countryId) {
      setIsLoading(true);
      axios
        .get(
          `/suppliersPlanning/dailyPlanning?country=${countryId}&date=${moment(
            startDate
          ).format("YYYY-MM-DD")}`
        )
        .then((response) => {
          if (response?.data?.dailyPlanning?.length > 0) {
            setPlanningData(response.data.dailyPlanning);
            setPageCount(response.data.filteredRecords);
          } else {
            setPlanningData([]);
            setPageCount(0);
          }
          setIsLoading(false);
          setIsRefresh(false);
        })
        .catch((error) => {});
    }
  };

  const onSolutionIdClick = (solutionId) => {
    setSolutionViewPopupVisibility(false);
    axios
      .get(`/solutions/${solutionId}`)
      .then((response) => {
        setCurrentSolution(response.data[0]);
        setSolutionViewPopupVisibility(true);
      })
      .catch((error) => handleApiError(error));
  };

  const onSolutionViewClose = () => {
    setSolutionViewPopupVisibility(false);
  };

  const closeDisputeViewPopup = () => {
    setViewDisputePopupVisible(false);
  };

  const fetchDate = (countryId) => {
    axios
      .get(`/general/nextBusinessDate?countries=[${countryId}]`)
      .then((response) => {
        if (page !== "site") {
          setStartDate(moment(response.data).format("YYYY-MM-DD"));
        }
        if (page === "site" && siteStartDate) {
          if (moment(siteStartDate, "YYYY-MM-DD", true).isValid()) {
            setStartDate(siteStartDate);
          } else {
            setStartDate(moment(siteStartDate).format("YYYY-MM-DD"));
          }
        }
        setEndDate(moment(response.data).format("YYYY-MM-DD"));
      })
      .catch((error) => {});
  };

  const onDisputeView = (lineId, siteId) => {
    setAddDisputeLineId(lineId);
    setAddDisputeSiteId(siteId);
    setViewDisputePopupVisible(true);
  };

  const onDisputeAdd = (lineId, siteId) => {
    setAddDisputeLineId(lineId);
    setAddDisputeSiteId(siteId);
    setAddDisputePopupVisible(true);
    setDisputeEditId("");
    setDisputeEdit(false);
    setDisputeInPopUp(false);
  };

  const onDisputeAddInPopUp = (lineId, siteId) => {
    setAddDisputeLineId(lineId);
    setAddDisputeSiteId(siteId);
    setViewDisputePopupVisible(false);
    setAddDisputePopupVisible(true);
    setDisputeEditId("");
    setDisputeEdit(false);
    setDisputeInPopUp(true);
  };

  const closeDisputeCreationPopup = () => {
    if (!isDisputeInPopUp) {
      setAddDisputeLineId("");
      setAddDisputeSiteId("");
      setDisputeEditId("");
    }
    if (isDisputeInPopUp) {
      setViewDisputePopupVisible(true);
    }
    setDisputeEdit(false);
    setAddDisputePopupVisible(false);
  };

  const onDisputeEdit = (disputeId) => {
    setDisputeEditId(disputeId);
    setDisputeEdit(true);
    setViewDisputePopupVisible(false);
    setAddDisputePopupVisible(true);
    setDisputeInPopUp(true);
  };

  const updateDisputeRefeshStatus = () => {
    setIsRefresh(true);
  };

  useEffect(() => {
    if (page === "global" && countryId && startDate && endDate) {
      fetchGlobalPlanning(countryId, startDate, endDate);
    }
    if (page === "site" && siteId && startDate && endDate) {
      fetchSitePlanning(siteId, startDate, endDate);
    }
    if (page === "daily" && countryId && startDate) {
      fetchDailyPlanning(countryId, startDate);
    }
  }, [startDate, countryId, endDate, currentPage, pageSize, pageStart]);

  const onStartDateChange = (e) => {
    setStartDate(moment(e.toDate()).format("YYYY-MM-DD"));
  };

  const onEndDateChange = (e) => {
    setEndDate(moment(e.toDate()).format("YYYY-MM-DD"));
  };

  useEffect(() => {
    if (Object.keys(generalParams).length !== 0) {
      if (!startDate) fetchDate(countryId);
    }
  }, [generalParams]);

  const onCountryChange = (e) => {
    setCountryId(e.value);
  };

  useEffect(() => {
    if (isRefresh) {
      if (page === "global") fetchGlobalPlanning();
      if (page === "site") fetchSitePlanning();
      if (page === "daily") fetchDailyPlanning();
    }
  }, [isRefresh]);

  const addPlanningNotification = () => {
    setIsAddPlanningNotification(true);
  };

  const closePlanningNotification = () => {
    setIsAddPlanningNotification(false);
  };

  const summaryAction = (isEdit, summaryId, itemId, jId) => {
    setAddSummaryPopupVisible(true);
    setSummaryId(summaryId);
    setClientOrderItemId(itemId);
    setEditSummary(isEdit);
    setJobsiteId(jId);
  };

  const closeSummaryCreationPopup = () => {
    setAddSummaryPopupVisible(false);
    setAddCentreSummaryPopupVisible(false);
    setAddHaulierSummaryPopupVisible(false);
    setSummaryId("");
    setClientOrderItemId("");
    setEditSummary(false);
    setJobsiteId("");
    setLineGroupId("");
    setCompanyId("");
    setCompanyName("");
  };

  const summaryByService = (
    isEdit,
    summaryId,
    itemId,
    jId,
    planningLineGroupId,
    hesusService,
    haulier,
    centre
  ) => {
    if (
      !hesusService?.needCombination &&
      !hesusService?.needCarrier &&
      hesusService?.needCenter
    ) {
      setAddCentreSummaryPopupVisible(true);
      setCompanyId(centre?.id);
      setCompanyName(centre?.name);
    } else {
      setAddHaulierSummaryPopupVisible(true);
      setCompanyId(haulier?.id);
      setCompanyName(haulier?.name);
    }

    setSummaryId(summaryId);
    setClientOrderItemId(itemId);
    setEditSummary(isEdit);
    setJobsiteId(jId);
    setLineGroupId(planningLineGroupId);
  };

  return (
    <>
      <PlanningListContainer className="planning-order-list">
        {page === "global" && <Title>{t("GLOBAL_PLANNING")}</Title>}
        <OuterWrapper>
          <FlexRow>
            <SectionContainer>
              <div>
                <FilterFlexRow>
                  <FilterOuter>
                    {page !== "site" && countryList?.length > 1 && (
                      <Dropdown
                        id="product-country"
                        label={t("CLIENT_ORDERS_FILTER_COUNTRY")}
                        name="country"
                        value={countryId}
                        onChange={onCountryChange}
                        defaultOption={t("COUNTRY")}
                        options={countryList}
                        labelStyles={labelStyles}
                      />
                    )}

                    <div className="planning-list__planning-date-container">
                      <label htmlFor="winning-date">
                        {page === "daily"
                          ? t("DAILY_PLANNING_FILTER_DATE")
                          : t(`${pageTranslation[page]}_FILTER_START_DATE`)}
                      </label>

                      <DatePicker
                        inputClass="planning-list__planning-date-input"
                        containerClassName="planning-list__planning-datepicker-container"
                        calendarPosition="bottom-center"
                        onChange={onStartDateChange}
                        format={getDatePickerFormat()}
                        locale={getCalendarLocale()}
                        value={getDateFormat(startDate, "YYYY-MM-DD") || ""}
                        name="winning-date"
                      />
                    </div>

                    {page === "daily" && (
                      <div className="status__filters-wrapper">
                        <Image
                          src={planingStatus ? enableIcon : disableIcon}
                          alt={t(
                            "DAILY_PLANNING_FILTER_ORDERS_WITH_SUPPLIERS_ONLY"
                          )}
                          title={t(
                            "DAILY_PLANNING_FILTER_ORDERS_WITH_SUPPLIERS_ONLY"
                          )}
                          marginLeft="7px"
                          onClick={() => changePlanningStatus()}
                          className="planning-list__general-planning-image-icon"
                        />
                        <label className="label warehouses-list__active-label">
                          {t(
                            "DAILY_PLANNING_FILTER_ORDERS_WITH_SUPPLIERS_ONLY"
                          )}
                        </label>
                      </div>
                    )}

                    {page !== "daily" && (
                      <div className="planning-list__planning-date-container">
                        <label htmlFor="winning-date">
                          {t(`${pageTranslation[page]}_FILTER_END_DATE`)}
                        </label>

                        <DatePicker
                          inputClass="planning-list__planning-date-input"
                          containerClassName="planning-list__planning-datepicker-container"
                          calendarPosition="bottom-center"
                          onChange={onEndDateChange}
                          format={getDatePickerFormat()}
                          locale={getCalendarLocale()}
                          value={getDateFormat(endDate, "YYYY-MM-DD") || ""}
                          name="winning-date"
                        />
                      </div>
                    )}
                  </FilterOuter>
                  {page === "daily" && startDate !== "" && (
                    <ButtonWrapper>
                      <ColumnButton
                        border="#1bace4"
                        background="#1bace4"
                        padding="10px"
                        onClick={() => addPlanningNotification()}
                      >
                        {t("ACTION_NOTIFY_SUPPLIERS")}
                      </ColumnButton>
                    </ButtonWrapper>
                  )}
                </FilterFlexRow>
              </div>
              <Header page={page} />
              <PlanningServiceInfo>
                <GridRow marginBottom="0px" gap="0rem">
                  <HeaderWrapperTable>
                    {isLoading && (
                      <div className="suggestions-list-loading">
                        <PulseLoader color="#1bace4" size={15} />
                      </div>
                    )}
                    {!isLoading && planningData?.length === 0 && (
                      <GridRow>
                        <GridSubLabel textAlign="center">
                          {t("NO_DATA")}
                        </GridSubLabel>
                      </GridRow>
                    )}

                    {!isLoading &&
                      planningData?.length > 0 &&
                      page === "global" && (
                        <GlobalListing
                          planningData={planningData}
                          onSolutionIdClick={onSolutionIdClick}
                          onDisputeAdd={onDisputeAdd}
                          onDisputeView={onDisputeView}
                          summaryByService={summaryByService}
                          planingStatus={planingStatus}
                          summaryAction={summaryAction}
                        />
                      )}
                    {!isLoading &&
                      planningData?.length > 0 &&
                      (page === "site" || page === "daily") && (
                        <SiteListing
                          planningData={planningData}
                          onSolutionIdClick={onSolutionIdClick}
                          siteId={siteId}
                          onDisputeAdd={onDisputeAdd}
                          onDisputeView={onDisputeView}
                          page={page}
                          planingStatus={planingStatus}
                          summaryAction={summaryAction}
                          summaryByService={summaryByService}
                        />
                      )}
                  </HeaderWrapperTable>
                </GridRow>
              </PlanningServiceInfo>
            </SectionContainer>
          </FlexRow>
          {isSolutionViewPopupVisible && (
            <SolutionView
              onClose={onSolutionViewClose}
              id={currentSolution.id}
              solutionData={currentSolution}
              isEditBtnVisible={false}
            />
          )}
          {isAddDisputePopupVisible && (
            <AddEditDispute
              id={disputeEditId}
              isEditMode={disputeEdit}
              siteId={addDisputeSiteId}
              planningId={addDisputeLineId}
              onClose={closeDisputeCreationPopup}
              updateDisputeRefeshStatus={updateDisputeRefeshStatus}
            />
          )}
          {isViewDisputePopupVisible && (
            <DisputeView
              planningId={addDisputeLineId}
              siteId={addDisputeSiteId}
              onClose={closeDisputeViewPopup}
              updateDisputeRefeshStatus={updateDisputeRefeshStatus}
              onDisputeAdd={onDisputeAddInPopUp}
              onDisputeEdit={onDisputeEdit}
            />
          )}
          {planningData.length !== 0 && pageCount > 100 && page !== "daily" && (
            <PaginationData
              pageCount={parseInt(pageCount, 10)}
              pageSize={pageSize}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              pageStart={pageStart}
              setPageStart={setPageStart}
              setPageSize={setPageSize}
            />
          )}
          {isAddSummaryPopupVisible && (
            <AddEditOtherSummary
              id={summaryId}
              clientOrderItemId={clientOrderItemId}
              isEditMode={editSummary}
              siteId={jobsiteId}
              onClose={closeSummaryCreationPopup}
              updateRefeshStatus={updateDisputeRefeshStatus}
            />
          )}
          {isAddHaulierSummaryPopupVisible && (
            <AddEditHaulierSummary
              id={summaryId}
              clientOrderItemId={clientOrderItemId}
              isEditMode={editSummary}
              siteId={jobsiteId}
              onClose={closeSummaryCreationPopup}
              updateRefreshStatus={updateDisputeRefeshStatus}
              haulierId={companyId}
              haulierName={companyName}
              lineGroupId={lineGroupId}
            />
          )}
          {isAddCentreSummaryPopupVisible && (
            <AddEditCentreSummary
              id={summaryId}
              clientOrderItemId={clientOrderItemId}
              isEditMode={editSummary}
              siteId={jobsiteId}
              onClose={closeSummaryCreationPopup}
              updateRefreshStatus={updateDisputeRefeshStatus}
              solutionId={companyId}
              centreName={companyName}
              lineGroupId={lineGroupId}
            />
          )}
        </OuterWrapper>
      </PlanningListContainer>
      {isAddPlanningNotification && (
        <AddEditPlanningNotification
          onClose={closePlanningNotification}
          planningDate={startDate}
          updateRefeshStatus={updateDisputeRefeshStatus}
          countryId={countryId}
        />
      )}
    </>
  );
};
GeneralPlanningList.propTypes = {
  t: PropTypes.func.isRequired,
  page: PropTypes.string.isRequired,
  siteId: PropTypes.number,
};
GeneralPlanningList.defaultProps = {
  siteId: null,
};
export default withTranslation()(GeneralPlanningList);
